'use client';
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Search, FileText, ChevronRight, BookOpen, Calendar,
  Facebook, MessageCircle, ArrowLeft, ScrollText, BarChart3, TrendingUp, Clapperboard, KeyRound, Radar, Rocket,
  Database,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  useMarkdownContent, getMarkdownFiles,
  extractTitle, extractDescription, parseRuleFilename, ensureKnowhowContent,
} from '../utils/markdown';
import VideoFactoryDemo from '../components/VideoFactoryDemo';
import MotherBaseKbTab from '../components/MotherBaseKbTab';

// ============================================================
// Knowhow Hub —— 集合式知識庫單頁（2026-08-22）
// 八個 TAB（2026-08-24 讀書庫升級為「得到庫」：讀書蒸餾＋博主蒸餾分區）
// Hash 路由：#reviews｜#insights｜#manual-facebook｜#facebook-setup｜#manual-cloudwapi｜#video-factory｜#library｜#rules
// ============================================================

type TabId = 'reviews' | 'insights' | 'manual-facebook' | 'facebook-setup' | 'manual-cloudwapi' | 'video-factory' | 'search-opt' | 'geo-upgrade' | 'geo-monthly' | 'ai-team-hub' | 'manual-ai-employee' | 'manual-aipa' | 'manual-ai-pm' | 'library' | 'motherbase-kb' | 'knowhow-modules' | 'rules';

const TABS: { id: TabId; label: string; icon: React.ReactNode }[] = [
  { id: 'reviews', label: '每週複檢', icon: <BarChart3 className="h-4 w-4" /> },
  { id: 'insights', label: '每週洞察', icon: <TrendingUp className="h-4 w-4" /> },
  { id: 'manual-facebook', label: 'Facebook 手冊', icon: <Facebook className="h-4 w-4" /> },
  { id: 'facebook-setup', label: 'FB 客戶接入', icon: <KeyRound className="h-4 w-4" /> },
  { id: 'manual-cloudwapi', label: 'WA 閘道手冊', icon: <MessageCircle className="h-4 w-4" /> },
  { id: 'video-factory', label: '影片模板工廠', icon: <Clapperboard className="h-4 w-4" /> },
  { id: 'search-opt', label: 'SEARCH-OPT 指揮官', icon: <Radar className="h-4 w-4" /> },
  { id: 'geo-upgrade', label: 'GEO 升級 Playbook', icon: <Rocket className="h-4 w-4" /> },
  { id: 'geo-monthly', label: 'GEO 月度匯報服務', icon: <BookOpen className="h-4 w-4" /> },
  { id: 'ai-team-hub', label: 'AI Team Hub 模板', icon: <BookOpen className="h-4 w-4" /> },
  { id: 'manual-ai-employee', label: 'AI 員工手冊', icon: <BookOpen className="h-4 w-4" /> },
  { id: 'manual-aipa', label: 'AIPA/AIPM 制度', icon: <BookOpen className="h-4 w-4" /> },
  { id: 'manual-ai-pm', label: 'AI 專案經理實錄', icon: <BookOpen className="h-4 w-4" /> },
  { id: 'library', label: '得到庫', icon: <BookOpen className="h-4 w-4" /> },
  { id: 'motherbase-kb', label: '母機知識庫', icon: <Database className="h-4 w-4" /> },
  { id: 'knowhow-modules', label: '知識軌', icon: <BookOpen className="h-4 w-4" /> },
  { id: 'rules', label: '母機守則', icon: <ScrollText className="h-4 w-4" /> },
];

const MANUAL_META: Record<string, { title: string; subtitle: string; file: string }> = {
  'manual-facebook': {
    title: 'Facebook API 對接手冊',
    subtitle: '快速取得 Page ID、Page Access Token、App ID、App Secret，無需重新摸索',
    file: '/src/content/knowhow/facebook.md',
  },
  'manual-cloudwapi': {
    title: '第三方 WhatsApp 閘道手冊',
    subtitle: 'API key／sender 配對、QR 裝置綁定、webhook 設定與實戰踩坑 FAQ',
    file: '/src/content/knowhow/cloudwapi.md',
  },
  'video-factory': {
    title: '影片模板工廠：Render-as-Code 閉環調試法',
    subtitle: '照片＋資料全自動變品牌影片——像素級校準方法論與三大實戰陷阱',
    file: '/src/content/knowhow/video-factory.md',
  },
  'search-opt': {
    title: 'SEARCH-OPT Commander：GEO/AEO/SEO 跨域優化指揮官',
    subtitle: '六階段 ReAct 診斷執行法＋建站即時實作清單——Agent 接到 AI 能見度需求照呢份直接做',
    file: '/src/content/knowhow/search-opt.md',
  },
  'geo-upgrade': {
    title: 'GEO 升級 Playbook：geo-scan 十八項標準',
    subtitle: '萬用提示詞＋秒速部署——填四個參數貼出去，≥80 分先准交貨（母機規則 183）',
    file: '/src/content/knowhow/geo-upgrade.md',
  },
  'geo-monthly': {
    title: 'GEO 月度匯報服務：標準化服務內容（SOP）',
    subtitle: '交付後每 30 天週期：自動採集→月報頁→草稿審批→客戶通知——新客戶七步接入照抄即用',
    file: '/src/content/knowhow/geo-monthly-service.md',
  },
  'ai-team-hub': {
    title: 'Role-Based AI Team Hub 模板（StudentHub 案例）',
    subtitle: '一個 AI 項目經理服務全團隊的角色化協作平台——學校否決私聊後的合規答案，五步複製到任何項目',
    file: '/src/content/knowhow/ai-team-hub.md',
  },
  'manual-ai-employee': {
    title: 'AI 員工實戰手冊：啟動・監控・經驗提煉',
    subtitle: 'AI 員工四要件＋啟動 SOP＋運行監控點＋三層經驗提煉協議——令下一個 AI 員工越做越精良（live：HKHTA AI 專案經理）',
    file: '/src/content/knowhow/ai-employee.md',
  },
  'manual-aipa': {
    title: 'AIPA／AIPM 個人工作助理制度',
    subtitle: '會議記錄進，項目指揮體系出——五步派生 AI 項目經理駐扎 Collab 資料夾，微信龍蝦bot 對客',
    file: '/src/content/knowhow/aipa.md',
  },
  'manual-ai-pm': {
    title: 'AI 專案經理實錄：第一個完整服務週期',
    subtitle: '客戶提出改動到收到「已完成」通知不足 36 小時——11 步生命週期＋三宗真實事故＋遷移清單',
    file: '/src/content/knowhow/ai-pm.md',
  },
};

function tabFromHash(): TabId {
  // SSR (Next SSG) has no window - fallback reviews (same as TABS default; hydration takes over via hashchange useEffect)
  const h = (typeof window !== 'undefined' ? window.location.hash : '').replace('#', '');
  return (TABS.some(t => t.id === h) ? h : 'reviews') as TabId;
}

// ---------- Markdown 渲染（統一排版 class；去掉內文首個 H1，外層已渲染標題） ----------
const stripLeadingTitle = (content: string): string =>
  content.replace(/^#\s+[^\n]+\n+/, '');

const MarkdownView: React.FC<{ content: string }> = ({ content }) => (
  <article className="knowhow-prose prose prose-invert max-w-none text-gray-300">
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{stripLeadingTitle(content)}</ReactMarkdown>
  </article>
);

// ---------- 手冊 TAB ----------
const ManualTab: React.FC<{ tabId: 'manual-facebook' | 'manual-cloudwapi' | 'video-factory' | 'search-opt' | 'geo-upgrade' | 'geo-monthly' | 'manual-ai-employee' | 'manual-aipa' | 'manual-ai-pm' | 'ai-team-hub' }> = ({ tabId }) => {
  const meta = MANUAL_META[tabId];
  const content = useMarkdownContent(meta.file);

  if (!content) {
    return <div className="py-20 text-center text-gray-500">內容載入中或不存在...</div>;
  }
  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">{meta.title}</h1>
        <p className="text-lg text-gray-400">{meta.subtitle}</p>
      </div>
      <MarkdownView content={content} />
    </div>
  );
};

// ---------- 母機守則 TAB ----------
interface RuleItem {
  number: string; name: string; file: string;
  title: string; description: string; content: string; category: string;
  tier: string;
}

const RULE_CATEGORIES: Record<string, string> = {
  '0': '基礎與思維', '1': '開發規範', '2': '平台與部署', '3': '流程與工作流',
  '4': '品質與規範', '5': '前端與狀態', '6': '知識與文檔', '7': '安全與憑證',
  '8': '營運與整合', '9': '特殊（98/99）',
};

function ruleCategory(number: string): string {
  const n = parseInt(number, 10);
  if (n <= 4) return RULE_CATEGORIES['0'];
  if (n <= 19) return RULE_CATEGORIES['1'];
  if (n <= 25) return RULE_CATEGORIES['2'];
  if (n <= 34) return RULE_CATEGORIES['3'];
  if (n <= 47) return RULE_CATEGORIES['4'];
  if (n <= 52) return RULE_CATEGORIES['5'];
  if (n <= 61) return RULE_CATEGORIES['6'];
  if (n <= 70) return RULE_CATEGORIES['7'];
  return RULE_CATEGORIES['9'];
}

// 從 frontmatter 抽欄位（category/tier）；缺欄回空字串
function fmField(content: string, key: string): string {
  const m = content.match(new RegExp(`^${key}:\\s*(.+)$`, 'm'));
  return m ? m[1].trim().replace(/^["']|["']$/g, '') : '';
}

const RULES_PAGE_SIZE = 50;

const RulesTab: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [domainFilter, setDomainFilter] = useState('全部');
  const [tierFilter, setTierFilter] = useState('全部');
  const [page, setPage] = useState(1);
  const [selectedRule, setSelectedRule] = useState<string | null>(null);

  const rules = useMemo(() => {
    const files = getMarkdownFiles('/src/content/rules');
    const items: RuleItem[] = [];
    for (const [filename, content] of Object.entries(files)) {
      const parsed = parseRuleFilename(filename);
      items.push({
        number: parsed.number,
        name: parsed.name,
        file: filename,
        title: extractTitle(content),
        description: extractDescription(content),
        content,
        category: fmField(content, 'category') || ruleCategory(parsed.number),
        tier: fmField(content, 'tier'),
      });
    }
    items.sort((a, b) => parseInt(a.number, 10) - parseInt(b.number, 10));
    return items;
  }, []);

  const domainCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const r of rules) counts[r.category] = (counts[r.category] || 0) + 1;
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [rules]);

  const filteredRules = useMemo(() => {
    let out = rules;
    if (domainFilter !== '全部') out = out.filter(r => r.category === domainFilter);
    if (tierFilter !== '全部') out = out.filter(r => r.tier === tierFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      out = out.filter(r =>
        r.title.toLowerCase().includes(q) ||
        r.name.toLowerCase().includes(q) ||
        r.description.toLowerCase().includes(q) ||
        r.content.toLowerCase().includes(q) ||
        r.number.includes(q)
      );
    }
    return out;
  }, [rules, searchQuery, domainFilter, tierFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredRules.length / RULES_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginatedRules = useMemo(
    () => filteredRules.slice((currentPage - 1) * RULES_PAGE_SIZE, currentPage * RULES_PAGE_SIZE),
    [filteredRules, currentPage]
  );

  const groupedRules = useMemo(() => {
    const groups: Record<string, RuleItem[]> = {};
    for (const rule of paginatedRules) {
      (groups[rule.category] ||= []).push(rule);
    }
    return groups;
  }, [paginatedRules]);

  const selectedRuleData = useMemo(
    () => (selectedRule ? rules.find(r => r.file === selectedRule) : null),
    [rules, selectedRule]
  );

  if (selectedRuleData) {
    return (
      <div>
        <button
          onClick={() => setSelectedRule(null)}
          className="mb-6 flex items-center gap-2 text-gray-400 hover:text-jkd-gold transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> 返回守則總覽
        </button>
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <span className="px-3 py-1 bg-jkd-gold/15 text-jkd-gold text-sm font-mono rounded">
              R{selectedRuleData.number.padStart(2, '0')}
            </span>
            <span className="text-gray-500 text-sm">{selectedRuleData.category}</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-white">{selectedRuleData.title}</h2>
        </div>
        <MarkdownView content={selectedRuleData.content} />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-2 flex items-center gap-3">
          <BookOpen className="h-8 w-8 text-jkd-gold" /> 母機守則
        </h2>
        <p className="text-gray-400">MotherBase+ 系統規則——AI 協作的核心規範與禁忌模式</p>
      </div>

      <div className="mb-8">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
          <input
            type="text"
            placeholder="搜索守則名稱、內容或編號..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
            className="w-full pl-10 pr-4 py-3 bg-jkd-gray-100 border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-jkd-gold"
          />
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-3">
          <select
            value={domainFilter}
            onChange={(e) => { setDomainFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 bg-jkd-gray-100 border border-gray-800 rounded-lg text-sm text-white focus:outline-none focus:border-jkd-gold"
          >
            <option value="全部">全部域（{rules.length}）</option>
            {domainCounts.map(([domain, count]) => (
              <option key={domain} value={domain}>{domain}（{count}）</option>
            ))}
          </select>
          <select
            value={tierFilter}
            onChange={(e) => { setTierFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 bg-jkd-gray-100 border border-gray-800 rounded-lg text-sm text-white focus:outline-none focus:border-jkd-gold"
          >
            <option value="全部">全部層級</option>
            <option value="core">core 核心層</option>
            <option value="domain">domain 領域層</option>
            <option value="case">case 個案層</option>
          </select>
          <p className="text-sm text-gray-500">
            共 {rules.length} 條｜篩選出 {filteredRules.length} 條｜第 {currentPage}/{totalPages} 頁
          </p>
        </div>
      </div>

      {Object.entries(groupedRules).map(([category, categoryRules]) => (
        <div key={category} className="mb-8">
          <h3 className="text-lg font-bold mb-3 text-gray-300 border-b border-gray-800 pb-2">
            {category}
            <span className="ml-2 text-sm font-normal text-gray-500">{categoryRules.length} 條</span>
          </h3>
          <div className="space-y-2">
            {categoryRules.map((rule) => (
              <button
                key={rule.file}
                onClick={() => setSelectedRule(rule.file)}
                className="w-full flex items-center gap-4 p-4 bg-jkd-gray-100 rounded-lg hover:bg-jkd-gold/5 transition-colors text-left group"
              >
                <span className="px-2 py-1 bg-black text-jkd-gold text-sm font-mono rounded shrink-0">
                  {rule.number.padStart(2, '0')}
                </span>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-white group-hover:text-jkd-gold transition-colors truncate">
                    {rule.title}
                  </h4>
                  {rule.description && (
                    <p className="text-sm text-gray-400 truncate">{rule.description}</p>
                  )}
                </div>
                <ChevronRight className="h-5 w-5 text-gray-600 group-hover:text-jkd-gold transition-colors shrink-0" />
              </button>
            ))}
          </div>
        </div>
      ))}

      {filteredRules.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>找不到符合條件的守則</p>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-8 pt-6 border-t border-gray-800">
          <button
            onClick={() => setPage(Math.max(1, currentPage - 1))}
            disabled={currentPage <= 1}
            className="px-4 py-2 bg-jkd-gray-100 border border-gray-800 rounded-lg text-sm text-white disabled:opacity-40 hover:border-jkd-gold transition-colors"
          >
            上一頁
          </button>
          <span className="text-sm text-gray-400">第 {currentPage} / {totalPages} 頁</span>
          <button
            onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage >= totalPages}
            className="px-4 py-2 bg-jkd-gray-100 border border-gray-800 rounded-lg text-sm text-white disabled:opacity-40 hover:border-jkd-gold transition-colors"
          >
            下一頁
          </button>
        </div>
      )}
    </div>
  );
};

// ---------- 知識軌 TAB（knowledge_base/modules/external 鏡像：海量參考模組） ----------
const KNOWLEDGE_GROUPS: Record<string, string> = {
  'tech-stack': '技術棧', 'agent-eng': '智能體工程',
  'eng-general': '工程通識', 'business-marketing': '商業行銷',
};

const KnowledgeModulesTab: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selected, setSelected] = useState<string | null>(null);

  const modules = useMemo(() => {
    const files = getMarkdownFiles('/src/content/knowhow-modules');
    const items: { file: string; group: string; groupLabel: string; title: string; desc: string; content: string }[] = [];
    for (const [filename, content] of Object.entries(files)) {
      const group = filename.split('/')[0] || 'other';
      const sourceMatch = content.match(/<!-- Source: ([^|]+)/);
      items.push({
        file: filename,
        group,
        groupLabel: KNOWLEDGE_GROUPS[group] || group,
        title: extractTitle(content) || filename.replace(/\.md$/, ''),
        desc: sourceMatch ? sourceMatch[1].trim() : extractDescription(content),
        content,
      });
    }
    items.sort((a, b) => a.file.localeCompare(b.file));
    return items;
  }, []);

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return modules;
    const q = searchQuery.toLowerCase();
    return modules.filter(m =>
      m.title.toLowerCase().includes(q) || m.desc.toLowerCase().includes(q) || m.content.toLowerCase().includes(q)
    );
  }, [modules, searchQuery]);

  const grouped = useMemo(() => {
    const groups: Record<string, typeof modules> = {};
    for (const m of filtered) (groups[m.groupLabel] ||= []).push(m);
    return groups;
  }, [filtered]);

  const selectedData = selected ? modules.find(m => m.file === selected) : null;

  if (selectedData) {
    return (
      <div>
        <button
          onClick={() => setSelected(null)}
          className="mb-6 flex items-center gap-2 text-gray-400 hover:text-jkd-gold transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> 返回知識軌總覽
        </button>
        <div className="mb-8">
          <span className="px-3 py-1 bg-jkd-gold/15 text-jkd-gold text-sm rounded">{selectedData.groupLabel}</span>
          <h2 className="text-2xl md:text-3xl font-bold text-white mt-3">{selectedData.title}</h2>
          {selectedData.desc && <p className="text-gray-500 text-sm mt-2">來源：{selectedData.desc}</p>}
        </div>
        <MarkdownView content={selectedData.content} />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-2 flex items-center gap-3">
          <Database className="h-8 w-8 text-jkd-gold" /> 知識軌
        </h2>
        <p className="text-gray-400">海量參考模組——外部實踐蒸餾（2026-10-24 起 156 條），與守則軌互補：守則精選可執行，知識軌支撐檢索深度</p>
      </div>

      <div className="mb-8">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
          <input
            type="text"
            placeholder="搜索模組標題、來源或內容..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-jkd-gray-100 border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-jkd-gold"
          />
        </div>
        <p className="text-sm text-gray-500 mt-2">
          共 {modules.length} 個模組{searchQuery && `，篩選出 ${filtered.length} 個`}
        </p>
      </div>

      {Object.entries(grouped).map(([groupLabel, items]) => (
        <div key={groupLabel} className="mb-8">
          <h3 className="text-lg font-bold mb-3 text-gray-300 border-b border-gray-800 pb-2">
            {groupLabel}
            <span className="ml-2 text-sm font-normal text-gray-500">{items.length} 個</span>
          </h3>
          <div className="space-y-2">
            {items.map((m) => (
              <button
                key={m.file}
                onClick={() => setSelected(m.file)}
                className="w-full flex items-center gap-4 p-4 bg-jkd-gray-100 rounded-lg hover:bg-jkd-gold/5 transition-colors text-left group"
              >
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-white group-hover:text-jkd-gold transition-colors truncate">{m.title}</h4>
                  {m.desc && <p className="text-sm text-gray-400 truncate">{m.desc}</p>}
                </div>
                <ChevronRight className="h-5 w-5 text-gray-600 group-hover:text-jkd-gold transition-colors shrink-0" />
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// ---------- 得到庫 TAB（GetNote 三年閱讀蒸餾：讀書＋博主分區；版權紅線：只放蒸餾產物，原文留本地） ----------
interface DistillCard {
  key: string; kind: 'book' | 'blogger'; file: string;
  title: string; theme: string; author: string;
  highlights: number; date: string; content: string; desc: string;
}

const THEME_ORDER = ['🧠 認知與思維模型', '🎯 影響力與說服', '⚔️ 東方智慧', '💪 心理韌性', '📊 商業與管理', '📝 內容與營銷'];

const parseFrontmatter = (raw: string): Record<string, string> => {
  const m = raw.match(/^---\r?\n([\s\S]*?)\r?\n---/);
  const out: Record<string, string> = {};
  if (m) {
    for (const line of m[1].split('\n')) {
      const kv = line.match(/^(\w+):\s*(.*)$/);
      if (kv) out[kv[1]] = kv[2].trim().replace(/^["']|["']$/g, '');
    }
  }
  return out;
};

const stripFrontmatter = (raw: string): string => raw.replace(/^---\r?\n[\s\S]*?\r?\n---\r?\n?/, '');

const LibraryTab: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const manual = useMarkdownContent('/src/content/knowhow/library/index.md');

  const cards = useMemo(() => {
    const list: DistillCard[] = [];
    const sections: Array<{ kind: 'book' | 'blogger'; dir: string }> = [
      { kind: 'book', dir: '/src/content/knowhow/library/books' },
      { kind: 'blogger', dir: '/src/content/knowhow/library/bloggers' },
    ];
    for (const { kind, dir } of sections) {
      const files = getMarkdownFiles(dir);
      for (const [filename, content] of Object.entries(files)) {
        const fm = parseFrontmatter(content as string);
        list.push({
          key: `${kind}/${filename}`,
          kind,
          file: filename,
          title: fm.title || extractTitle(content as string),
          theme: fm.theme || (kind === 'blogger' ? '🎬 博主蒸餾' : '未分類'),
          author: fm.author || '',
          highlights: parseInt(fm.highlights || fm.posts || '0', 10) || 0,
          date: fm.date || '',
          desc: extractDescription(stripFrontmatter(content as string)),
          content: content as string,
        });
      }
    }
    return list;
  }, []);

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return cards;
    const q = searchQuery.toLowerCase();
    return cards.filter(b =>
      b.title.toLowerCase().includes(q) ||
      b.author.toLowerCase().includes(q) ||
      b.theme.toLowerCase().includes(q) ||
      b.content.toLowerCase().includes(q)
    );
  }, [cards, searchQuery]);

  const books = filtered.filter(c => c.kind === 'book');
  const bloggers = filtered.filter(c => c.kind === 'blogger');

  const groupedBooks = useMemo(() => {
    const groups: Record<string, DistillCard[]> = {};
    for (const b of books) {
      (groups[b.theme] ||= []).push(b);
    }
    return Object.entries(groups).sort(
      ([a], [b]) => {
        const ia = THEME_ORDER.indexOf(a), ib = THEME_ORDER.indexOf(b);
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      }
    );
  }, [books]);

  const selectedData = selected ? cards.find(b => b.key === selected) : null;

  if (selectedData) {
    return (
      <div>
        <button
          onClick={() => setSelected(null)}
          className="mb-6 flex items-center gap-2 text-gray-400 hover:text-jkd-gold transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> 返回得到庫
        </button>
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <span className="px-3 py-1 bg-jkd-gold/15 text-jkd-gold text-sm rounded">{selectedData.theme}</span>
            <span className="text-gray-500 text-sm">{selectedData.author}</span>
            <span className="text-gray-500 text-sm font-mono">
              {selectedData.kind === 'blogger' ? `${selectedData.highlights} 篇內容` : `${selectedData.highlights} 條劃線`} · {selectedData.date}
            </span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-white">{selectedData.title}</h2>
        </div>
        <MarkdownView content={stripFrontmatter(selectedData.content)} />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-2 flex items-center gap-3">
          <BookOpen className="h-8 w-8 text-jkd-gold" /> 得到庫
        </h2>
        <p className="text-gray-400">
          得到 App 三年閱讀存量（2,004 條書籍劃線、8 位博主 3,741 篇訂閱）的蒸餾庫——論點、方法論與行動啟發；書卡附劃線原文，博主原文直達
        </p>
      </div>

      {manual && (
        <details className="mb-8 p-5 rounded-xl border border-jkd-gold/30 bg-jkd-gold/5">
          <summary className="cursor-pointer text-jkd-gold font-semibold select-none">
            📖 使用手冊：怎麼用這個庫（不用記，要用時來拿）
          </summary>
          <div className="mt-4">
            <MarkdownView content={manual} />
          </div>
        </details>
      )}

      <div className="mb-8">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
          <input
            type="text"
            placeholder="搜索書名、作者、博主、方法論..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-jkd-gray-100 border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-jkd-gold"
          />
        </div>
        <p className="text-sm text-gray-500 mt-2">
          📚 讀書蒸餾已上線 {books.length}／63 本 · 🎬 博主蒸餾已上線 {bloggers.length}／8 位
        </p>
      </div>

      {groupedBooks.map(([theme, themeBooks]) => (
        <div key={theme} className="mb-8">
          <h3 className="text-lg font-bold mb-3 text-gray-300 border-b border-gray-800 pb-2">
            {theme}
            <span className="ml-2 text-sm font-normal text-gray-500">{themeBooks.length} 本</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {themeBooks.map(b => (
              <button
                key={b.key}
                onClick={() => setSelected(b.key)}
                className="p-5 bg-jkd-gray-100 rounded-lg hover:bg-jkd-gold/5 transition-colors text-left group border border-gray-800 hover:border-jkd-gold/30"
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <h4 className="font-semibold text-white group-hover:text-jkd-gold transition-colors leading-snug">
                    {b.title}
                  </h4>
                  <ChevronRight className="h-5 w-5 text-gray-600 group-hover:text-jkd-gold transition-colors shrink-0 mt-0.5" />
                </div>
                <p className="text-sm text-gray-400 mb-3 line-clamp-2">{b.desc}</p>
                <div className="flex items-center gap-2 text-xs flex-wrap">
                  <span className="px-2 py-1 bg-black/40 text-gray-300 rounded-full">{b.author}</span>
                  <span className="px-2 py-1 bg-black/40 text-jkd-gold/80 rounded-full font-mono">{b.highlights} 劃線</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      ))}

      <div className="mb-8">
        <h3 className="text-lg font-bold mb-3 text-gray-300 border-b border-gray-800 pb-2">
          🎬 博主蒸餾
          <span className="ml-2 text-sm font-normal text-gray-500">{bloggers.length} 位</span>
        </h3>
        {bloggers.length === 0 ? (
          <div className="p-6 bg-jkd-gray-100 rounded-lg border border-gray-800 text-center">
            <p className="text-gray-400 mb-1">8 位博主（罗振宇、王山水、王铭苇、许同学、玄九尘、胖哥扯淡、老柴说、硬核小说家杨五哥）</p>
            <p className="text-sm text-gray-500 mb-4">蒸餾卡製作中——原文全集已先上線，點下方直達</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {bloggers.map(b => (
              <button
                key={b.key}
                onClick={() => setSelected(b.key)}
                className="p-5 bg-jkd-gray-100 rounded-lg hover:bg-jkd-gold/5 transition-colors text-left group border border-gray-800 hover:border-jkd-gold/30"
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <h4 className="font-semibold text-white group-hover:text-jkd-gold transition-colors leading-snug">
                    {b.title}
                  </h4>
                  <ChevronRight className="h-5 w-5 text-gray-600 group-hover:text-jkd-gold transition-colors shrink-0 mt-0.5" />
                </div>
                <p className="text-sm text-gray-400 mb-3 line-clamp-2">{b.desc}</p>
                <div className="flex items-center gap-2 text-xs flex-wrap">
                  <span className="px-2 py-1 bg-black/40 text-gray-300 rounded-full">{b.author}</span>
                  <span className="px-2 py-1 bg-black/40 text-jkd-gold/80 rounded-full font-mono">{b.highlights} 篇</span>
                </div>
              </button>
            ))}
          </div>
        )}
        <div className="mt-4">
          <p className="text-sm text-gray-500 mb-2">📄 原文直達（訂閱內容全集，自用版權政策 2026-08-24）：</p>
          <div className="flex flex-wrap gap-2">
            {['罗振宇', '王山水', '王铭苇', '许同学', '玄九尘', '胖哥扯淡', '老柴说', '硬核小说家杨五哥'].map(name => (
              <a
                key={name}
                href={`/dedao/bloggers/${name}.md`}
                target="_blank"
                rel="noreferrer"
                className="px-3 py-1.5 bg-jkd-gray-100 border border-gray-800 rounded-lg text-sm text-gray-300 hover:text-jkd-gold hover:border-jkd-gold/40 transition-colors"
              >
                {name}
              </a>
            ))}
          </div>
        </div>
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>找不到符合「{searchQuery}」的內容</p>
        </div>
      )}
    </div>
  );
};

// ---------- 系統運作導讀（僅複檢 TAB；預設折疊，點擊才展開——2026-08-23 Captain 指示） ----------
const SystemIntro: React.FC = () => {
  const intro = useMarkdownContent('/src/content/system/pipeline-intro.md');
  const tasks = useMarkdownContent('/src/content/system/scheduled_tasks.md');
  const [open, setOpen] = useState(false);
  const [showFull, setShowFull] = useState(false);
  if (!intro) return null;
  return (
    <div className="mb-8">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-5 py-3 bg-jkd-gray-100/60 border border-gray-800 rounded-lg text-sm font-semibold text-gray-300 hover:text-white hover:border-jkd-gold/40 transition-colors"
      >
        <span>⚙️ 系統運作說明（管線 / 安全模型 / 常駐服務）</span>
        <span className="text-gray-500">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="mt-3 p-5 bg-jkd-gray-100/60 border border-gray-800 rounded-lg">
          <div className="knowhow-prose prose prose-invert max-w-none text-sm text-gray-300">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{stripLeadingTitle(intro)}</ReactMarkdown>
          </div>
          <button
            onClick={() => setShowFull(v => !v)}
            className="mt-4 px-4 py-2 border border-gray-600 text-gray-400 text-sm rounded-lg hover:text-white hover:border-jkd-gold/50 transition-colors"
          >
            {showFull ? '▲ 收起完整任務盤點表' : '▼ 展開完整任務盤點表（含路徑與依賴）'}
          </button>
          {showFull && tasks && (
            <div className="mt-4 pt-4 border-t border-gray-800">
              <MarkdownView content={tasks} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ---------- 每週複檢 TAB（落實工作台 v2：預設全選採納，反勾否決，一鍵 DeepSeek 落實） ----------
interface ReviewItem {
  id: string;
  project: string;
  title: string;
  date: string;
  raw: string;
}

interface ProcessingRecord {
  statsLine: string;
  tableLines: string[];
}

interface ReviewBlock {
  type: 'prose' | 'item';
  content?: string;   // prose 用
  item?: ReviewItem;  // item 用
}

// 解析報告為「原文順序」區塊流：prose（摘要/建議/清單等）與 item（#### 決策項）交錯，
// 前端按原順序渲染——審計即操作，無需切換（2026-08-23 Captain 指示取消雙視圖）
function parseReview(content: string): { blocks: ReviewBlock[]; items: ReviewItem[]; record: ProcessingRecord | null } {
  const lines = content.split('\n');
  const blocks: ReviewBlock[] = [];
  const items: ReviewItem[] = [];
  let recordLines: string[] = [];
  let inDetail = false;
  let inRecord = false;
  let currentProject = '';
  let currentItem: { title: string; buf: string[] } | null = null;
  let proseBuf: string[] = [];

  const flushProse = () => {
    const t = proseBuf.join('\n').trim();
    if (t) blocks.push({ type: 'prose', content: t });
    proseBuf = [];
  };

  const flushItem = () => {
    if (currentItem) {
      const raw = currentItem.buf.join('\n').trim();
      const dateMatch = raw.match(/日期：(\S+)/);
      const item: ReviewItem = {
        id: `${currentProject}::${currentItem.title}`,
        project: currentProject,
        title: currentItem.title,
        date: dateMatch ? dateMatch[1] : '',
        raw,
      };
      items.push(item);
      blocks.push({ type: 'item', item });
      currentItem = null;
    }
  };

  for (const line of lines) {
    if (line.startsWith('## ')) {
      flushItem();
      flushProse();
      inDetail = false;
      if (line.includes('詳細問題與守則建議')) { inDetail = true; continue; }
      if (line.includes('處理記錄')) { inRecord = true; continue; }
      inRecord = false;
      proseBuf.push(line);
      continue;
    }
    if (inRecord) { recordLines.push(line); continue; }
    if (!inDetail) { proseBuf.push(line); continue; }
    if (line.startsWith('### ')) { flushItem(); currentProject = line.replace(/^###\s+/, '').trim(); continue; }
    if (line.startsWith('#### ')) { flushItem(); currentItem = { title: line.replace(/^####\s+/, '').trim(), buf: [] }; continue; }
    if (currentItem) currentItem.buf.push(line);
  }
  flushItem();
  flushProse();

  let record: ProcessingRecord | null = null;
  if (recordLines.length) {
    const statsLine = (recordLines.find(l => l.includes('統計：')) || '').replace(/\*/g, '');
    record = { statsLine, tableLines: recordLines.filter(l => l.startsWith('|')) };
  }
  return { blocks, items, record };
}

const EXECUTOR_URL = 'http://127.0.0.1:5050';

interface ApplyResult {
  ok: boolean;
  stats?: Record<string, number>;
  results?: { title: string; disposition: string; outcome: string }[];
  pipeline?: Record<string, string>;
  error?: string;
}

const ReviewsTab: React.FC = () => {
  const [selectedReview, setSelectedReview] = useState<string | null>(null);
  const [checks, setChecks] = useState<Record<string, boolean>>({});
  const [executorUp, setExecutorUp] = useState<boolean | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [applyResult, setApplyResult] = useState<ApplyResult | null>(null);
  const [rolling, setRolling] = useState(false);
  const [rollbackMsg, setRollbackMsg] = useState<string | null>(null);

  const doRollback = async (reportDate: string) => {
    if (!window.confirm(`回滾 ${reportDate} 的全部落實？（刪自動新守則／還原強化／刪知識庫條目，報告回到人手操作狀態）`)) return;
    setRolling(true);
    setRollbackMsg(null);
    try {
      const resp = await fetch(`${EXECUTOR_URL}/api/review/rollback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ report_date: reportDate }),
      });
      const d = await resp.json();
      if (d.ok) {
        setRollbackMsg(
          `✓ 已回滾：${(d.undone || []).join('；') || '無檔案變更'}。網站約 1 分鐘後更新，重新整理本頁即可回到「待落實」人手操作狀態。`,
        );
      } else {
        setRollbackMsg(`回滾失敗：${d.error}（需在母機上操作）`);
      }
    } catch (e) {
      setRollbackMsg(`執行器連線失敗：${e}（需在母機上操作且 port 5050 已啟動）`);
    } finally {
      setRolling(false);
    }
  };

  const checkExecutor = useCallback(() => {
    setExecutorUp(null);
    fetch(`${EXECUTOR_URL}/health`, { mode: 'cors' })
      .then(r => r.ok ? setExecutorUp(true) : setExecutorUp(false))
      .catch(() => setExecutorUp(false));
  }, []);

  useEffect(() => {
    if (selectedReview) checkExecutor();
  }, [selectedReview, checkExecutor]);

  const reviews = useMemo(() => {
    const files = getMarkdownFiles('/src/content/reviews');
    return Object.entries(files)
      .map(([filename, content]) => {
        const m = filename.match(/(\d{4}-\d{2}-\d{2})/);
        return { file: filename, date: m ? m[1] : filename.replace('.md', ''), content: content as string };
      })
      .sort((a, b) => b.date.localeCompare(a.date));
  }, []);

  const selectedReviewData = useMemo(
    () => (selectedReview ? reviews.find(r => r.file === selectedReview) : null),
    [reviews, selectedReview]
  );

  const openReview = (file: string) => {
    setSelectedReview(file);
    setApplyResult(null);
    setChecks({}); // 進入時全部預設採納
  };

  const statsLineOf = (r: ApplyResult): string => {
    const s = r.stats || {};
    return `採納 ${s.adopted ?? 0} · 否決 ${s.rejected ?? 0}（新守則 ${s.A ?? 0}／強化 ${s.B ?? 0}／知識庫 ${s.C ?? 0}／跳過 ${s.D ?? 0}）`;
  };

  const confirmApply = async (items: ReviewItem[], reportDate: string) => {
    setSubmitting(true);
    setApplyResult(null);
    try {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), 15 * 60 * 1000);
      const resp = await fetch(`${EXECUTOR_URL}/api/review/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          report_date: reportDate,
          items: items.map(it => ({
            id: it.id, project: it.project, title: it.title, date: it.date, raw: it.raw,
            action: checks[it.id] === false ? 'reject' : 'adopt',
          })),
        }),
        signal: ctrl.signal,
      });
      clearTimeout(timer);
      setApplyResult(await resp.json());
    } catch (e) {
      setApplyResult({ ok: false, error: `執行器連線失敗：${e}。請確認在母機上操作且 port 5050 已啟動` });
    } finally {
      setSubmitting(false);
    }
  };

  if (selectedReviewData) {
    const { blocks, items, record } = parseReview(selectedReviewData.content);
    const processed = !!record;
    const adoptedCount = items.filter(it => checks[it.id] !== false).length;

    return (
      <div>
        <button
          onClick={() => { setSelectedReview(null); setApplyResult(null); }}
          className="mb-6 flex items-center gap-2 text-gray-400 hover:text-jkd-gold transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> 返回複檢報告列表
        </button>
        <div className="mb-6">
          <span className="inline-block px-3 py-1 bg-jkd-gold/15 text-jkd-gold text-sm font-mono rounded mb-3">
            {selectedReviewData.date}
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-white">每週複檢 · 落實工作台</h2>
        </div>

        {processed ? (
          <div className="mb-8 p-5 bg-green-950/30 border border-green-600/30 rounded-lg">
            <p className="font-semibold text-green-400 mb-2">✓ 本報告已落實</p>
            <p className="text-gray-300 mb-3">{record!.statsLine}</p>
            <div className="mb-3">
              <button
                onClick={() => doRollback(selectedReviewData.date)}
                disabled={rolling}
                className="px-4 py-2 border border-red-500/50 text-red-400 text-sm font-semibold rounded-lg hover:bg-red-500/10 transition-colors disabled:opacity-40"
              >
                {rolling ? '⏳ 回滾中…' : '↩ 回滾（撤銷落實，轉人手操作）'}
              </button>
              {rollbackMsg && <p className="mt-2 text-sm text-gray-400">{rollbackMsg}</p>}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <tbody>
                  {record!.tableLines.filter(l => !l.match(/^\|-+\|/)).map((l, i) => {
                    const cells = l.split('|').filter(c => c.trim());
                    return (
                      <tr key={i} className="border-b border-gray-800">
                        {cells.map((c, j) => <td key={j} className={`py-1.5 px-2 ${j === 0 ? 'text-gray-300' : 'text-gray-400'}`}>{c.trim()}</td>)}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="mb-6 p-4 bg-jkd-gold/5 border border-jkd-gold/20 rounded-lg text-sm text-gray-300">
            <p className="mb-2 font-semibold text-jkd-gold">預設全部採納——勾掉你不要的，按確定一鍵落實</p>
            <p className="mb-3">
              確定後：每項交 DeepSeek 判定（新守則／強化既有守則／知識庫／跳過）→ 自動寫入 SOULS（含備份）→
              同步 rag.jkdcoding.com → 重建部署 jkdcoding.com。結果沉澱回本報告。
            </p>
            {executorUp === false && (
              <p className="text-red-400 mb-2">
                ⚠ 執行器未連上（需在母機上操作，且 port 5050 已啟動：scripts\start_review_workbench.bat）——
                <button onClick={checkExecutor} className="underline hover:text-white">↻ 重試連線</button>
              </p>
            )}
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-gray-400">採納 {adoptedCount}／{items.length} · 否決 {items.length - adoptedCount}</span>
              <button onClick={() => setChecks(Object.fromEntries(items.map(it => [it.id, true])))}
                className="px-3 py-1 border border-gray-600 rounded hover:text-white text-gray-400">全選</button>
              <button onClick={() => setChecks(Object.fromEntries(items.map(it => [it.id, false])))}
                className="px-3 py-1 border border-gray-600 rounded hover:text-white text-gray-400">全不選</button>
            </div>
          </div>
        )}

        {!processed && (
          <div className="mb-6">
            <button
              onClick={() => confirmApply(items, selectedReviewData.date)}
              disabled={submitting || executorUp === false}
              className="px-6 py-3 bg-jkd-gold text-black font-bold rounded-lg hover:bg-yellow-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submitting ? '⏳ DeepSeek 落實中…（每項約 5-20 秒，請勿關閉頁面）' : `✔ 確定：落實 ${adoptedCount} 項＋同步所有地方`}
            </button>
          </div>
        )}

        {applyResult && (
          <div className={`mb-8 p-5 rounded-lg border ${applyResult.ok ? 'bg-green-950/30 border-green-600/30' : 'bg-red-950/30 border-red-600/30'}`}>
            <p className={`font-semibold mb-3 ${applyResult.ok ? 'text-green-400' : 'text-red-400'}`}>
              {applyResult.ok ? `落實完成：${statsLineOf(applyResult)}` : `失敗：${applyResult.error}`}
            </p>
            {applyResult.ok && applyResult.results && (
              <div className="space-y-1 text-sm mb-3">
                {applyResult.results.map((r, i) => (
                  <p key={i} className="text-gray-300">
                    <span className="font-mono text-jkd-gold mr-2">{r.disposition}</span>
                    {r.title} <span className="text-gray-500">→ {r.outcome}</span>
                  </p>
                ))}
              </div>
            )}
            {applyResult.ok && applyResult.pipeline && (
              <p className="text-xs text-gray-500">
                同步管線：{Object.entries(applyResult.pipeline).map(([k, v]) => `${k}=${v}`).join(' · ')}
                （網站重建部署約需 1 分鐘生效，重新整理本頁可見沉澱結果）
              </p>
            )}
          </div>
        )}

        {/* 原文順序渲染：prose 段落與決策項卡片交錯（審計即操作，單一視圖） */}
        <div className="space-y-6">
          {blocks.map((b, i) => {
            if (b.type === 'prose') {
              return (
                <div key={`p${i}`} className="knowhow-prose prose prose-invert max-w-none text-sm text-gray-300">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{b.content!}</ReactMarkdown>
                </div>
              );
            }
            const it = b.item!;
            const unchecked = !processed && checks[it.id] === false;
            return (
              <div key={it.id} className={`p-5 bg-jkd-gray-100 rounded-lg border ${processed ? 'border-gray-800' : unchecked ? 'border-gray-700 opacity-55' : 'border-jkd-gold/25'}`}>
                <div className="flex items-start gap-3">
                  {!processed && (
                    <input
                      type="checkbox"
                      checked={checks[it.id] !== false}
                      onChange={e => setChecks(prev => ({ ...prev, [it.id]: e.target.checked }))}
                      className="mt-1.5 h-5 w-5 accent-jkd-gold shrink-0 cursor-pointer"
                      aria-label="採納"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="px-2 py-0.5 bg-black text-jkd-gold text-xs font-mono rounded">{it.project}</span>
                      {it.date && <span className="px-2 py-0.5 bg-gray-800 text-gray-400 text-xs font-mono rounded">{it.date}</span>}
                    </div>
                    <h4 className={`font-semibold text-white mb-3 ${unchecked ? 'line-through decoration-red-500/60' : ''}`}>
                      {it.title}
                    </h4>
                    <div className="knowhow-prose prose prose-invert max-w-none text-sm text-gray-300">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{it.raw}</ReactMarkdown>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-2 flex items-center gap-3">
          <Calendar className="h-8 w-8 text-jkd-gold" /> 每週複檢報告
        </h2>
        <p className="text-gray-400">點開報告 → 預設全選採納 → 反勾不要的 → 一鍵 DeepSeek 落實＋同步</p>
      </div>

      <SystemIntro />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {reviews.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>暫無複檢報告</p>
            <p className="text-sm mt-2">每週日 22:00 自動生成</p>
          </div>
        ) : (
          reviews.map((review) => {
            const { items, record } = parseReview(review.content);
            const m = record?.statsLine.match(/採納 (\d+) 項.*否決 (\d+) 項/) || null;
            const abcd = record?.statsLine.match(/A 新守則 (\d+)／B 強化 (\d+)／C 知識庫 (\d+)／D 跳過 (\d+)(?:／G 缺口新守則 (\d+))?/) || null;
            return (
              <button
                key={review.file}
                onClick={() => openReview(review.file)}
                className="w-full p-5 bg-jkd-gray-100 rounded-lg hover:bg-jkd-gold/5 transition-colors text-left group border border-gray-800 hover:border-jkd-gold/30"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-2xl font-bold font-mono text-jkd-gold">{review.date}</span>
                    {record ? (
                      <span className="px-2.5 py-0.5 bg-green-900/40 text-green-400 text-xs font-semibold rounded-full border border-green-600/30">✓ 已落實</span>
                    ) : (
                      <span className="px-2.5 py-0.5 bg-jkd-gold/10 text-jkd-gold text-xs font-semibold rounded-full border border-jkd-gold/30">待落實</span>
                    )}
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-600 group-hover:text-jkd-gold transition-colors shrink-0" />
                </div>

                {record && m ? (
                  <div className="flex gap-2 flex-wrap mb-3 text-xs">
                    <span className="px-2 py-1 bg-black/40 text-gray-300 rounded-full">共 {items.length} 項</span>
                    <span className="px-2 py-1 bg-green-900/30 text-green-400 rounded-full">採納 {m[1]}</span>
                    <span className="px-2 py-1 bg-gray-800 text-gray-400 rounded-full">否決 {m[2]}</span>
                    {abcd && (
                      <span className="px-2 py-1 bg-gray-800 text-gray-400 rounded-full">
                        新守則 {abcd[1]} · 強化 {abcd[2]} · 知識庫 {abcd[3]} · 跳過 {abcd[4]}{abcd[5] ? ` · 缺口 ${abcd[5]}` : ''}
                      </span>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 mb-3">
                    {items.length > 0 ? `共 ${items.length} 項決策記錄 · 預設全採納` : '暫無條目'}
                  </p>
                )}

                {items.length > 0 && (
                  <div className="space-y-1 text-sm text-gray-500">
                    {items.slice(0, 3).map(it => (
                      <p key={it.id} className="truncate">· {it.title}</p>
                    ))}
                    {items.length > 3 && <p className="text-gray-600">…另有 {items.length - 3} 項</p>}
                  </div>
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

// ---------- 每週洞察 TAB（週溫度摘要：只觀察零修改） ----------
interface InsightItem {
  file: string; date: string; range: string;
  sessions: string; hits: string; content: string;
}

const InsightsTab: React.FC = () => {
  const [selected, setSelected] = useState<string | null>(null);

  const items = useMemo(() => {
    const files = getMarkdownFiles('/src/content/insights');
    const list: InsightItem[] = [];
    for (const [filename, content] of Object.entries(files)) {
      const m = filename.match(/(\d{4}-\d{2}-\d{2})/);
      const rangeM = content.match(/^# 週溫度摘要 (\S+) ~ (\S+)$/m);
      const sessM = content.match(/\| 會話總數 \| ([^|]+)\|/);
      const hitsM = content.match(/\| 規則命中 \| ([^|]+)\|/);
      list.push({
        file: filename,
        date: m ? m[1] : filename.replace('.md', ''),
        range: rangeM ? `${rangeM[1]}~${rangeM[2]}` : '',
        sessions: sessM ? sessM[1].trim() : '?',
        hits: hitsM ? hitsM[1].trim() : '?',
        content: content as string,
      });
    }
    return list.sort((a, b) => b.date.localeCompare(a.date));
  }, []);

  const selectedData = selected ? items.find(i => i.file === selected) : null;

  if (selectedData) {
    return (
      <div>
        <button
          onClick={() => setSelected(null)}
          className="mb-6 flex items-center gap-2 text-gray-400 hover:text-jkd-gold transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> 返回洞察列表
        </button>
        <MarkdownView content={selectedData.content} />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-2 flex items-center gap-3">
          <TrendingUp className="h-8 w-8 text-jkd-gold" /> 每週洞察（溫度計）
        </h2>
        <p className="text-gray-400">每週日 22:00 回掃 7 天會話 → DeepSeek 週分析——只觀察，收割見複檢工作台</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map(it => (
          <button
            key={it.file}
            onClick={() => setSelected(it.file)}
            className="p-5 bg-jkd-gray-100 rounded-lg hover:bg-jkd-gold/5 transition-colors text-left group border border-gray-800 hover:border-jkd-gold/30"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-lg font-bold font-mono text-jkd-gold">{it.range || it.date}</span>
              <ChevronRight className="h-5 w-5 text-gray-600 group-hover:text-jkd-gold transition-colors shrink-0" />
            </div>
            <div className="flex gap-2 flex-wrap text-xs">
              <span className="px-2 py-1 bg-black/40 text-gray-300 rounded-full">會話 {it.sessions}</span>
              <span className="px-2 py-1 bg-black/40 text-gray-300 rounded-full">命中 {it.hits}</span>
            </div>
          </button>
        ))}
        {items.length === 0 && (
          <div className="col-span-full text-center py-12 text-gray-500">
            <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>暫無洞察</p>
            <p className="text-sm mt-2">每週日 22:00 自動生成</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------- FB 客戶接入工作台 TAB（帳密代辦批量模式：瀏覽器三步 → API 接力 → 金庫） ----------
interface FbPage {
  id: string; name: string; tasks: string[];
}

interface FbResult {
  ok: boolean;
  customer?: string;
  app_id?: string;
  pages?: FbPage[];
  token_expires_in?: number | null;
  granted?: string[];
  vault_written?: boolean;
  vault_path?: string;
  screenshots_dir?: string;
  warning?: string;
  error?: string;
}

const fbInstruction = (customer: string): string =>
  `【FB 接入任務：${customer}】在本地瀏覽器完成三步，產出 App ID／App Secret／短期 User Token（手冊：jkdcoding.com/knowhow#manual-facebook）：
① 登入 facebook.com —— 用「${customer}」專屬瀏覽器 profile；密碼由 Captain 親自輸入，禁止寫入任何檔案／log／備註；2FA、簡訊碼、「是你本人嗎」等安全閘口：停下來在對話描述畫面，等 Captain 回覆，絕不猜碼
② developers.facebook.com → 建立 App（Business 類型）→ Settings → Basic：記下 App ID 與 App Secret（如要求帳號驗證＝閘口）
③ Graph API Explorer → 右上角選該 App → Generate Access Token → 勾權限（dev-mode 免審查，一次勾齊免得日後補權限要重領 token）：
   基本包：pages_show_list、pages_read_engagement、pages_manage_posts、pages_manage_metadata、pages_read_user_content、pages_manage_engagement、read_insights
   廣告包（要投廣告必勾）：ads_management、business_management
   通訊包（可選）：pages_messaging
   → Continue as ${customer} 同意授權 → 複製產生的短期 token
規則：擬人節奏（頁面載入後停 2-5 秒、逐欄填寫）；禁止改密碼／加減管理員／動廣告資產與付款設定／刪任何東西；每個關鍵畫面截圖存 E:\\Projects\\motherbase\\data\\fb_setup\\${customer}_{日期}\\
完成後把 App ID／App Secret／短期 Token 三項貼回工作台「段二」按執行，其餘全自動（換長期 token → 撈專頁 ID＋永久 Page Token → 寫金庫）。`;

const FbSetupTab: React.FC = () => {
  const [customer, setCustomer] = useState('');
  const [appId, setAppId] = useState('');
  const [appSecret, setAppSecret] = useState('');
  const [shortToken, setShortToken] = useState('');
  const [executorUp, setExecutorUp] = useState<boolean | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<FbResult | null>(null);
  const [copied, setCopied] = useState(false);

  const checkExecutor = useCallback(() => {
    setExecutorUp(null);
    fetch(`${EXECUTOR_URL}/health`, { mode: 'cors' })
      .then(r => r.ok ? setExecutorUp(true) : setExecutorUp(false))
      .catch(() => setExecutorUp(false));
  }, []);

  useEffect(() => { checkExecutor(); }, [checkExecutor]);

  const copyInstruction = async () => {
    try {
      await navigator.clipboard.writeText(fbInstruction(customer.trim() || '{客戶名}'));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard 不可用時可手動選取 */ }
  };

  const submit = async () => {
    setSubmitting(true);
    setResult(null);
    try {
      const resp = await fetch(`${EXECUTOR_URL}/api/fb/onboard`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer: customer.trim(),
          app_id: appId.trim(),
          app_secret: appSecret.trim(),
          short_token: shortToken.trim(),
        }),
      });
      setResult(await resp.json());
    } catch (e) {
      setResult({ ok: false, error: `執行器連線失敗：${e}（需在母機上操作且 port 5050 已啟動）` });
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = 'w-full px-4 py-3 bg-jkd-gray-100 border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-jkd-gold';
  const canSubmit = !submitting && executorUp !== false
    && customer.trim() && appId.trim() && appSecret.trim() && shortToken.trim();

  const expiresNote = result?.token_expires_in
    ? `約 ${Math.round(result.token_expires_in / 86400)} 天後到期（僅影響 user 級操作）`
    : '長期（未提供到期資訊）';

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-2 flex items-center gap-3">
          <KeyRound className="h-8 w-8 text-jkd-gold" /> FB 客戶接入工作台
        </h2>
        <p className="text-gray-400">
          客戶只給帳密，其餘代辦——瀏覽器三步（登入→建 App→領 token）→ 本台一鍵接力（換長期 token→撈專頁→寫金庫）
        </p>
      </div>

      {executorUp === false && (
        <div className="mb-6 p-4 bg-red-950/30 border border-red-600/30 rounded-lg text-sm text-red-400">
          ⚠ 執行器未連上（需在母機上操作，且 port 5050 已啟動：scripts\start_review_workbench.bat）——
          <button onClick={checkExecutor} className="underline hover:text-white ml-1">↻ 重試連線</button>
        </div>
      )}

      {/* 段一：接單 */}
      <div className="mb-6 p-5 bg-jkd-gray-100 rounded-lg border border-gray-800">
        <p className="font-semibold text-jkd-gold mb-1">段一 · 接單</p>
        <p className="text-sm text-gray-400 mb-3">輸入客戶名，生成瀏覽器階段指令（自己照做，或貼給 ZCode／Kimi／任何智能體代跑）</p>
        <div className="flex gap-3 flex-wrap">
          <input
            type="text" value={customer} onChange={e => setCustomer(e.target.value)}
            placeholder="客戶名（例如：金晉物業代理）" className={`${inputCls} flex-1 min-w-64`}
          />
          <button
            onClick={copyInstruction}
            className="px-5 py-3 border border-jkd-gold/50 text-jkd-gold font-semibold rounded-lg hover:bg-jkd-gold/10 transition-colors whitespace-nowrap"
          >
            {copied ? '✓ 已複製' : '📋 複製瀏覽器階段指令'}
          </button>
        </div>
      </div>

      {/* 段二：交接 */}
      <div className="mb-6 p-5 bg-jkd-gray-100 rounded-lg border border-jkd-gold/25">
        <p className="font-semibold text-jkd-gold mb-1">段二 · 交接</p>
        <p className="text-sm text-gray-400 mb-3">
          貼上瀏覽器階段產出的三項 → 一鍵執行。秘密只走母機本地執行器（127.0.0.1:5050），不經任何公開服務、瀏覽器不留存
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <input type="text" value={appId} onChange={e => setAppId(e.target.value)} placeholder="App ID（數字）" className={inputCls} />
          <input type="password" value={appSecret} onChange={e => setAppSecret(e.target.value)} placeholder="App Secret" className={inputCls} autoComplete="off" />
          <input type="password" value={shortToken} onChange={e => setShortToken(e.target.value)} placeholder="短期 User Token（EA…開頭）" className={inputCls} autoComplete="off" />
        </div>
        <button
          onClick={submit} disabled={!canSubmit}
          className="px-6 py-3 bg-jkd-gold text-black font-bold rounded-lg hover:bg-yellow-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {submitting ? '⏳ 執行中…（交換 token→撈專頁→寫金庫）' : '✔ 執行：換長期 Token＋撈專頁＋寫金庫'}
        </button>
      </div>

      {/* 段三：交付 */}
      {result && (
        <div className={`p-5 rounded-lg border ${result.ok ? 'bg-green-950/30 border-green-600/30' : 'bg-red-950/30 border-red-600/30'}`}>
          <p className={`font-semibold mb-3 ${result.ok ? 'text-green-400' : 'text-red-400'}`}>
            {result.ok ? `接入完成：${result.customer}` : `失敗：${result.error}`}
          </p>
          {result.ok && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-sm text-gray-300 mb-4">
                <p>✓ App ID <span className="font-mono text-jkd-gold">{result.app_id}</span></p>
                <p>✓ App Secret（已入金庫）</p>
                <p>✓ User Token（{expiresNote}）</p>
                <p>✓ 權限核對：{result.granted?.length ?? 0} 項已授予</p>
                <p>✓ 專頁 {result.pages?.length ?? 0} 個（Page ID＋永久 Page Token 已入金庫）</p>
                <p>{result.vault_written ? `✓ 金庫已寫：${result.vault_path}` : '⚠ 金庫未寫（dry-run）'}</p>
              </div>
              {result.warning && <p className="mb-3 text-amber-400 text-sm">⚠ {result.warning}</p>}
              {!!result.pages?.length && (
                <div className="overflow-x-auto mb-3">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="border-b border-gray-700 text-gray-400">
                        <th className="text-left py-1.5 px-2">專頁</th>
                        <th className="text-left py-1.5 px-2">Page ID</th>
                        <th className="text-left py-1.5 px-2">帳號層權限</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.pages.map(p => (
                        <tr key={p.id} className="border-b border-gray-800">
                          <td className="py-1.5 px-2 text-white">{p.name}</td>
                          <td className="py-1.5 px-2 font-mono text-jkd-gold">{p.id}</td>
                          <td className="py-1.5 px-2 text-gray-400">{p.tasks.join(', ')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {result.granted?.length ? (
                <p className="text-xs text-gray-500">已授予權限：{result.granted.join(' · ')}</p>
              ) : null}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ---------- 主頁面 ----------
// 2026-09-04 安全升級：前端 1014 token 閘退役——改由 Cloudflare Access（edge 登入）
const KnowhowHubPage: React.FC = () => {
  const [tab, setTab] = useState<TabId>(tabFromHash);
  const [contentReady, setContentReady] = useState(false);
  const [contentError, setContentError] = useState<string | null>(null);

  useEffect(() => {
    const onHash = () => setTab(tabFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    ensureKnowhowContent()
      .then(() => setContentReady(true))
      .catch((e: Error) => setContentError(e.message));
  }, []);

  const switchTab = useCallback((id: TabId) => {
    setTab(id);
    window.history.replaceState(null, '', `#${id}`);
    window.scrollTo({ top: 0 });
  }, []);

  if (contentError) {
    return (
      <div className="min-h-screen bg-jkd-black text-white flex items-center justify-center px-4">
        <div className="w-full max-w-md p-8 rounded-2xl border border-gray-800 bg-jkd-gray-100/60 text-center">
          <KeyRound className="h-10 w-10 text-jkd-gold mx-auto mb-4" />
          <h1 className="text-xl font-bold mb-2">內容載入失敗</h1>
          <p className="text-gray-400 mb-4">{contentError}</p>
          <p className="text-sm text-gray-500">請確認已透過 Cloudflare Access 登入（重新整理會自動跳轉登入頁）。</p>
          <button
            onClick={() => location.reload()}
            className="mt-6 px-6 py-3 rounded-lg bg-jkd-gold text-jkd-black font-bold hover:opacity-90 transition-opacity"
          >
            重新整理
          </button>
        </div>
      </div>
    );
  }

  if (!contentReady) {
    return (
      <div className="min-h-screen bg-jkd-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-2 border-jkd-gold border-t-transparent mb-4" />
          <p className="text-gray-400">正在從保險庫載入內容…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-jkd-black text-white">
      {/* 頂部橫幅 */}
      <header className="border-b border-gray-800 bg-gradient-to-b from-jkd-gray-100/60 to-transparent">
        <div className="container mx-auto px-4 pt-12 pb-6 max-w-5xl">
          <h1 className="text-4xl md:text-5xl font-bold mb-3">
            <span className="bg-gradient-gold bg-clip-text text-transparent">Knowhow</span>{' '}
            <span className="text-white">知識庫</span>
          </h1>
          <p className="text-lg text-gray-400">
            技術對接手冊、母機守則與每週複檢——一頁集中管理
          </p>
        </div>
      </header>

      {/* 黏性 TAB 列 */}
      <nav className="sticky top-0 z-20 bg-jkd-black/95 backdrop-blur border-b border-gray-800">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="flex gap-1 overflow-x-auto" role="tablist">
            {TABS.map(t => (
              <button
                key={t.id}
                role="tab"
                aria-selected={tab === t.id}
                onClick={() => switchTab(t.id)}
                className={`flex items-center gap-2 px-5 py-4 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${
                  tab === t.id
                    ? 'text-jkd-gold border-jkd-gold'
                    : 'text-gray-400 border-transparent hover:text-white hover:border-gray-600'
                }`}
              >
                {t.icon}{t.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* 內容區 */}
      <main className="container mx-auto px-4 py-10 max-w-5xl">
        {tab === 'manual-facebook' && <ManualTab tabId="manual-facebook" />}
        {tab === 'facebook-setup' && <FbSetupTab />}
        {tab === 'manual-cloudwapi' && <ManualTab tabId="manual-cloudwapi" />}
        {tab === 'video-factory' && (
          <>
            <ManualTab tabId="video-factory" />
            <VideoFactoryDemo />
          </>
        )}
        {tab === 'search-opt' && <ManualTab tabId="search-opt" />}
        {tab === 'geo-upgrade' && <ManualTab tabId="geo-upgrade" />}
        {tab === 'geo-monthly' && <ManualTab tabId="geo-monthly" />}
        {tab === 'ai-team-hub' && <ManualTab tabId="ai-team-hub" />}
        {tab === 'manual-ai-employee' && <ManualTab tabId="manual-ai-employee" />}
        {tab === 'manual-aipa' && <ManualTab tabId="manual-aipa" />}
        {tab === 'rules' && <RulesTab />}
        {tab === 'knowhow-modules' && <KnowledgeModulesTab />}
        {tab === 'reviews' && <ReviewsTab />}
        {tab === 'insights' && <InsightsTab />}
        {tab === 'library' && <LibraryTab />}
        {tab === 'motherbase-kb' && <MotherBaseKbTab />}
      </main>

      <footer className="border-t border-gray-800 py-6 text-center text-sm text-gray-600">
        MotherBase+ Knowhow Hub · 守則變更經 git commit 自動同步部署
      </footer>
    </div>
  );
};

export default KnowhowHubPage;
